import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, RefreshCw, AlertCircle, Search, X, Check } from "lucide-react";

import { dashboardService } from "../../services/api";
import tpIcon from "../../assets/images/tp-icon.png";
import tpLogo from "../../assets/images/tp-logo.png";
import CupBracket, { CupLegend, CUP_BRACKET_CSS } from "./CupBracket";
// Shared standings building blocks — extracted verbatim into a common module
// (Competitions build 2026-08-17) so the Competitions pages reuse them without
// duplication. Behaviour identical to the previous widget-local definitions.
import {
  initials,
  Flag,
  FormGuide,
  TeamLogo,
  CompetitionLogo,
} from "../common/standingsVisuals";

// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────
const TP = {
  primary:  "#4A90D9",
  primaryDark: "#2F78C4",
  primarySoft: "#EBF3FF",
  text:     "#1A1A2E",
  text2:    "#6B7280",
  text3:    "#9CA3AF",
  border:   "#E5E7EB",
  bgSubtle: "#FAFAFA",
  headerBg: "#F9FAFB",
  hover:    "#EBF3FF",
  win:      "#10B981",
  draw:     "#9CA3AF",
  loss:     "#EF4444",
};

const REFRESH_NORMAL_MS = 5 * 60 * 1000;  // 5 min, no live matches
const REFRESH_LIVE_MS   = 30 * 1000;       // 30 s when at least one match is live

// German display labels for our canonical promotion-zone codes (the backend
// already sends OUR design-palette colour on `promotion.color`). When a zone
// code is not in this map we fall back to the backend-provided `promotion.name`.
const ZONE_LABELS = {
  cl:         "Champions League",
  el:         "Europa League",
  conf:       "Conference",
  rel:        "Abstieg",
  rel_po:     "Relegations-Playoff",
  prom:       "Aufstieg",
  prom_po:    "Aufstiegs-Playoff",
  euq:        "Titel-/Euro-Quali",
  po:         "Playoff",
  // Cup winner's continental berth (light blue). Labelled in English to match
  // the European-competition labels above (Champions/Europa/Conference League).
  cup_winner: "Cup winner",
};

// ─────────────────────────────────────────────────────────────────────────────
// Tiny helpers
// ─────────────────────────────────────────────────────────────────────────────
function normalize(s) { return (s || "").toLowerCase(); }

// ─────────────────────────────────────────────────────────────────────────────
// Generic sub-components
// ─────────────────────────────────────────────────────────────────────────────
function LivePip({ color = TP.loss, size = 8 }) {
  return (
    <span
      className="tp-livepip relative inline-block flex-shrink-0"
      style={{ "--lp": color, width: size, height: size }}
    >
      <span />
      <span />
    </span>
  );
}

function LivePill({ count, tone = "loss" }) {
  const color = tone === "loss" ? TP.loss : TP.win;
  const bg = tone === "loss" ? "rgba(239,68,68,0.10)" : "rgba(16,185,129,0.10)";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-extrabold tracking-wider"
      style={{ color, background: bg }}
    >
      <LivePip color={color} />
      {count != null ? `${count} LIVE` : "LIVE"}
    </span>
  );
}

// (FormDot/FormGuide/TeamLogo/CompetitionLogo moved verbatim to
// ../common/standingsVisuals — imported above.)

// ─────────────────────────────────────────────────────────────────────────────
// LeagueDropdown — Search + Top5 chips + grouped regions
// ─────────────────────────────────────────────────────────────────────────────
function LeagueDropdown({ regions, activeId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  // Panel is rendered into document.body via a Portal so the widget's
  // overflow:hidden doesn't clip it. Position is computed from the trigger
  // button's rect on open + scroll + resize.
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0, maxHeight: 480 });
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const SAFE_MARGIN = 12;
    const GAP_BELOW_TRIGGER = 8;
    const top = rect.bottom + GAP_BELOW_TRIGGER;
    const right = Math.max(SAFE_MARGIN, window.innerWidth - rect.right);
    const maxHeight = Math.max(220, window.innerHeight - top - SAFE_MARGIN);
    setPanelPos({ top, right, maxHeight: Math.min(maxHeight, 480) });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    computePos();
    const onScroll = () => computePos();
    const onResize = () => computePos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, computePos]);

  const flatLeagues = useMemo(() => {
    const all = [];
    for (const r of regions || []) {
      for (const l of r.leagues || []) all.push({ ...l, _region: r.label });
    }
    return all;
  }, [regions]);

  const active = useMemo(
    () => flatLeagues.find((l) => l.competition_id === activeId),
    [flatLeagues, activeId],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inWrap && !inPanel) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [open]);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const top5 = useMemo(() => {
    const r = (regions || []).find((x) => x.id === "top5");
    return r?.leagues || [];
  }, [regions]);

  const q = query.trim().toLowerCase();
  const filteredByRegion = useMemo(() => {
    if (!q) return regions || [];
    const filt = [];
    for (const r of regions || []) {
      const matches = (r.leagues || []).filter((l) =>
        normalize(l.name).includes(q) ||
        normalize(l.country).includes(q) ||
        normalize(l.country_code).includes(q) ||
        normalize(l.short_name).includes(q),
      );
      if (matches.length) filt.push({ ...r, leagues: matches });
    }
    return filt;
  }, [regions, q]);

  const pick = (l) => { onSelect(l); setOpen(false); };

  return (
    <div className="relative flex-shrink-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2.5 rounded-lg border bg-white pl-1.5 pr-2.5 py-1.5 transition-all"
        style={{
          borderColor: open ? TP.primary : TP.border,
          boxShadow: open ? "0 0 0 3px rgba(74,144,217,0.18)" : "none",
          minWidth: 200,
        }}
      >
        <CompetitionLogo league={active} size={28} />
        <div className="flex flex-col leading-tight text-left min-w-0 flex-1">
          <span className="text-[13px] font-bold truncate" style={{ color: TP.text }}>
            {active?.name || "Choose league"}
          </span>
          <span className="text-[10px] font-medium truncate" style={{ color: TP.text2 }}>
            {active ? `${active.country} · ${active.season_year || "—"}` : "—"}
          </span>
        </div>
        {active?.live_count > 0 && <LivePill count={active.live_count} tone="loss" />}
        <ChevronDown
          size={14}
          style={{ color: TP.text2, transform: open ? "rotate(180deg)" : "none", transition: "transform .18s" }}
        />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          className="fixed rounded-xl border bg-white flex flex-col overflow-hidden"
          style={{
            top: panelPos.top,
            right: panelPos.right,
            width: 340,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: panelPos.maxHeight,
            zIndex: 9999,
            borderColor: TP.border,
            boxShadow: "0 16px 48px rgba(20,40,80,0.18), 0 0 0 1px rgba(20,40,80,0.04)",
          }}
        >
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: TP.border, color: TP.text2 }}>
            <Search size={14} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search league or country…"
              className="flex-1 bg-transparent text-sm font-medium outline-none"
              style={{ color: TP.text }}
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                aria-label="Clear"
                className="w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70"
                style={{ background: TP.bgSubtle, color: TP.text2 }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Top-5 chips (only when no search query) */}
          {!q && top5.length > 0 && (
            <div className="px-3 pt-2.5 pb-2 border-b" style={{ borderColor: TP.border, background: TP.bgSubtle }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider pb-1.5" style={{ color: TP.text3 }}>
                Top 5
              </div>
              <div className="flex flex-wrap gap-1.5">
                {top5.map((l) => {
                  const isActive = l.competition_id === activeId;
                  return (
                    <button
                      key={l.competition_id}
                      type="button"
                      onClick={() => pick(l)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition-all"
                      style={
                        isActive
                          ? {
                              background: TP.primary, color: "#fff",
                              borderColor: TP.primary,
                              boxShadow: "0 4px 12px rgba(74,144,217,0.28)",
                            }
                          : { background: "#fff", color: TP.text, borderColor: TP.border }
                      }
                    >
                      <Flag league={l} size={14} />
                      <span>{l.name}</span>
                      {l.live_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold"
                          style={{
                            background: isActive ? "rgba(255,255,255,0.18)" : "rgba(239,68,68,0.10)",
                            color: isActive ? "#fff" : TP.loss,
                          }}
                        >
                          <LivePip color={isActive ? "#fff" : TP.loss} size={6} />
                          {l.live_count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Region list */}
          <div className="flex-1 overflow-y-auto p-1.5">
            {filteredByRegion.length === 0 && (
              <div className="px-4 py-6 text-center text-sm" style={{ color: TP.text2 }}>
                No matches for "{query}"
              </div>
            )}
            {filteredByRegion.map((r) => {
              if (!q && r.id === "top5") return null;
              return (
                <div key={r.id} className="mb-1">
                  <div
                    className="text-[10px] font-extrabold uppercase tracking-wider px-2 pt-2 pb-1"
                    style={{ color: TP.text3 }}
                  >
                    {r.label}
                  </div>
                  {r.leagues.map((l) => {
                    const isActive = l.competition_id === activeId;
                    return (
                      <button
                        key={l.competition_id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => pick(l)}
                        className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors"
                        style={{ background: isActive ? TP.hover : "transparent" }}
                      >
                        <span className="w-7 text-center flex-shrink-0 inline-flex items-center justify-center">
                          <Flag league={l} size={20} />
                        </span>
                        <span className="flex flex-col leading-tight flex-1 min-w-0">
                          <span className="text-[13px] font-bold truncate" style={{ color: TP.text }}>
                            {l.name}
                          </span>
                          <span className="text-[11px] font-medium truncate" style={{ color: TP.text2 }}>
                            {l.country} · {l.season_year || "—"}
                          </span>
                        </span>
                        {l.live_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold"
                            style={{ background: "rgba(239,68,68,0.10)", color: TP.loss }}
                          >
                            <LivePip color={TP.loss} size={6} />
                            {l.live_count}
                          </span>
                        )}
                        {isActive && <Check size={14} style={{ color: TP.primary }} />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Topbar (brand + dropdown)
// ─────────────────────────────────────────────────────────────────────────────
function Topbar({ regions, activeLeague, onSelectLeague }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 border-b"
      style={{ borderColor: TP.border, background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)" }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <img src={tpIcon} alt="" className="w-9 h-9 rounded-lg flex-shrink-0" />
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-[10px] font-extrabold uppercase tracking-wider" style={{ color: TP.primary }}>
            TransferPitch
          </span>
          <span className="text-[15px] font-extrabold truncate" style={{ color: TP.text }}>
            Standings
          </span>
        </div>
      </div>
      <LeagueDropdown
        regions={regions}
        activeId={activeLeague?.competition_id}
        onSelect={onSelectLeague}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LeagueHeader — logo + name + country/season + LIVE pill
// ─────────────────────────────────────────────────────────────────────────────
function LeagueHeader({ league }) {
  if (!league) return null;
  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 border-b"
      style={{ borderColor: TP.border, background: "#fff" }}
    >
      <CompetitionLogo league={league} size={32} />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-bold truncate" style={{ color: TP.text }}>
          {league.name}
        </div>
        <div className="text-[11px] font-medium" style={{ color: TP.text2 }}>
          {league.country} · {league.season_year || "—"}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LiveStrip — shown only when matches.length > 0
// ─────────────────────────────────────────────────────────────────────────────
function LiveStrip({ matches, matchday }) {
  const railRef = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);

  const update = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  }, [update, matches?.length]);

  if (!matches || matches.length === 0) return null;

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: "smooth" });
  };

  return (
    <div
      className="flex items-stretch gap-3 px-4 py-2.5 border-b"
      style={{
        borderColor: TP.border,
        background: "linear-gradient(180deg, rgba(239,68,68,0.04) 0%, rgba(239,68,68,0.00) 100%), #fff",
      }}
    >
      <div
        className="flex flex-col justify-center gap-1 pr-3 border-r flex-shrink-0"
        style={{ borderColor: TP.border }}
      >
        <LivePill count={matches.length} />
        <span className="text-[11px] font-semibold" style={{ color: TP.text2 }}>
          {matchday ? `Matchday ${matchday} live` : "Live now"}
        </span>
      </div>
      <div className="relative flex-1 min-w-0 flex items-center">
        {canL && (
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="w-7 h-7 rounded-full border bg-white inline-flex items-center justify-center flex-shrink-0 z-10 -mr-2"
            style={{ borderColor: TP.border, boxShadow: "0 4px 12px rgba(20,40,80,0.10)" }}
          >
            <ChevronDown size={14} style={{ transform: "rotate(90deg)" }} />
          </button>
        )}
        <div
          ref={railRef}
          className="flex gap-2 overflow-x-auto flex-1 min-w-0 py-0.5"
          style={{ scrollbarWidth: "none", scrollSnapType: "x proximity" }}
        >
          {matches.map((m) => <LiveMatchCard key={m.id} match={m} />)}
        </div>
        {canR && (
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="w-7 h-7 rounded-full border bg-white inline-flex items-center justify-center flex-shrink-0 z-10 -ml-2"
            style={{ borderColor: TP.border, boxShadow: "0 4px 12px rgba(20,40,80,0.10)" }}
          >
            <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
          </button>
        )}
      </div>
    </div>
  );
}

function LiveMatchCard({ match }) {
  // TheSports doesn't return the running minute on TSMatch — estimate
  // from match_time (Unix seconds) for status 2 (first half) / 4 (second half).
  const minuteLabel = useMemo(() => {
    if (match.minute_label) return match.minute_label;
    const t = match.match_time;
    if (!t) return "LIVE";
    const elapsedMin = Math.max(0, Math.floor((Date.now() / 1000 - t) / 60));
    if (match.status_id === 2) return `${Math.min(elapsedMin, 45)}'`;
    if (match.status_id === 4) return `${Math.min(45 + Math.max(elapsedMin - 60, 0), 90)}'`;
    if (match.status_id === 5 || match.status_id === 7) return "ET";
    if (match.status_id === 9) return "Pen";
    return "LIVE";
  }, [match]);

  const home = match.home || {};
  const away = match.away || {};
  const score = match.score || {};

  return (
    <div
      className="flex items-center gap-3 px-3 py-1.5 rounded-lg border bg-white flex-shrink-0"
      style={{ borderColor: TP.border, scrollSnapAlign: "start" }}
    >
      <div className="text-[11px] font-extrabold tracking-wider min-w-[28px] tabular-nums" style={{ color: TP.loss }}>
        {minuteLabel}
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: TP.text }}>
          <TeamLogo team={home} size={16} />
          <span className="tracking-wide">{home.short_name || initials(home.name)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: TP.text }}>
          <TeamLogo team={away} size={16} />
          <span className="tracking-wide">{away.short_name || initials(away.name)}</span>
        </div>
      </div>
      <div
        className="flex flex-col items-end pl-2.5 border-l text-[14px] font-extrabold tabular-nums"
        style={{ borderColor: TP.border, color: TP.text }}
      >
        <span>{score.home ?? "—"}</span>
        <span>{score.away ?? "—"}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Matchday bar (refresh)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// LastSeasonBanner — visible only when the data we're showing is not the
// current season (the upstream sync hasn't promoted the new season yet).
// ─────────────────────────────────────────────────────────────────────────────
function LastSeasonBanner({ seasonYear }) {
  return (
    <div
      className="flex items-start gap-2 px-4 py-2 border-b text-[11px]"
      style={{
        borderColor: TP.border,
        background: "rgba(245,158,11,0.08)",
        color: "#92400E",
      }}
    >
      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1, color: "#B45309" }} />
      <div className="flex-1 min-w-0 leading-snug">
        <span className="font-bold">
          {seasonYear ? `Last season · ${seasonYear}` : "Last season"}
        </span>
        <span className="ml-1">
          Showing the previous season. Current season data is not available yet.
        </span>
      </div>
    </div>
  );
}

function MatchdayBar({ matchday, secondsAgo, onRefresh, refreshing }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-2 border-b text-xs"
      style={{ borderColor: TP.border, background: TP.bgSubtle, color: TP.text2 }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {matchday > 0 && (
          <>
            <span
              className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full border bg-white"
              style={{ color: TP.text, borderColor: TP.border }}
            >
              Matchday {matchday}
            </span>
            <span style={{ color: TP.text3 }}>·</span>
          </>
        )}
        <span className="truncate">
          {secondsAgo == null ? "Loading…" : `Updated ${secondsAgo}s ago`}
        </span>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh"
        aria-label="Refresh"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md border bg-white disabled:opacity-50 transition-colors"
        style={{ borderColor: TP.border, color: TP.text2 }}
      >
        <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CupBar — the cup counterpart of MatchdayBar (no matchday/live for a knockout):
// shows the season pill, the cup-state legend, and the refresh control.
// ─────────────────────────────────────────────────────────────────────────────
function CupBar({ season, secondsAgo, onRefresh, refreshing }) {
  return (
    <div
      className="flex items-center justify-between px-4 py-2 border-b text-xs"
      style={{ borderColor: TP.border, background: TP.bgSubtle, color: TP.text2 }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {season && (
          <>
            <span
              className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full border bg-white"
              style={{ color: TP.text, borderColor: TP.border }}
            >
              Season {season}
            </span>
            <span style={{ color: TP.text3 }}>·</span>
          </>
        )}
        <CupLegend />
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh"
        aria-label="Refresh"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md border bg-white disabled:opacity-50 transition-colors"
        style={{ borderColor: TP.border, color: TP.text2 }}
      >
        <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
      </button>
    </div>
  );
}

// CupFooter — the cup counterpart of Footer (no promotion-zone legend; the cup
// state legend lives in CupBar). Keeps the "Powered by" mark for consistency.
function CupFooter() {
  return (
    <div
      className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t"
      style={{ borderColor: TP.border, background: TP.bgSubtle }}
    >
      <div className="text-[11px] font-medium" style={{ color: TP.text2 }}>
        The bracket updates automatically throughout the season.
      </div>
      <div className="inline-flex items-center gap-2 text-[11px] font-medium" style={{ color: TP.text2 }}>
        <span>Powered by</span>
        <img src={tpLogo} alt="TransferPitch" height={16} style={{ height: 16, opacity: 0.95 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StandingsTable
// ─────────────────────────────────────────────────────────────────────────────
function StandingsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm py-8" style={{ color: TP.text2 }}>
        No standings data available
      </div>
    );
  }
  const cellBorder = `1px solid rgba(229,231,235,0.6)`;
  return (
    <div className="overflow-x-auto overflow-y-auto h-full">
      <table className="w-full text-[13px]" style={{ fontVariantNumeric: "tabular-nums" }}>
        <thead>
          <tr style={{ background: TP.headerBg, color: TP.text2 }}>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 pl-4 pr-1" style={{ borderBottom: `1px solid ${TP.border}`, width: 56 }}>#</th>
            <th className="text-left   font-bold uppercase tracking-wider text-[11px] py-2.5 px-1"      style={{ borderBottom: `1px solid ${TP.border}` }}>Team</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1"      style={{ borderBottom: `1px solid ${TP.border}` }}>SP</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden sm:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>S</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden sm:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>U</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden sm:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>N</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden md:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>T</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden md:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>GT</th>
            <th className="text-center font-bold uppercase tracking-wider text-[11px] py-2.5 px-1 hidden md:table-cell" style={{ borderBottom: `1px solid ${TP.border}` }}>TD</th>
            <th className="text-right  font-bold uppercase tracking-wider text-[11px] py-2.5 pr-4 pl-1" style={{ borderBottom: `1px solid ${TP.border}` }}>Pts</th>
            <th className="text-right  font-bold uppercase tracking-wider text-[11px] py-2.5 pr-4 pl-1 hidden lg:table-cell" style={{ borderBottom: `1px solid ${TP.border}`, width: 96 }}>Form</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const stripeColor = r.promotion?.color || "transparent";
            const gdColor = r.goal_diff > 0 ? TP.win : r.goal_diff < 0 ? TP.loss : TP.text2;
            const stripeBg = idx % 2 === 0 ? "#fff" : TP.bgSubtle;
            return (
              <tr key={r.team?.id || `pos-${r.position}`} style={{ background: stripeBg }}>
                <td className="text-left pl-4 pr-1 py-2 align-middle" style={{ borderBottom: cellBorder }}>
                  <div className="inline-flex items-center gap-2.5">
                    <span
                      className="inline-block flex-shrink-0"
                      style={{ width: 3, height: 24, borderRadius: 2, background: stripeColor }}
                      title={r.promotion?.name || ""}
                      aria-label={r.promotion?.name || ""}
                    />
                    <span className="font-semibold inline-block text-center" style={{ minWidth: 18, color: TP.text }}>
                      {r.position}
                    </span>
                  </div>
                </td>
                <td className="text-left px-1 py-2 align-middle" style={{ borderBottom: cellBorder }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <TeamLogo team={r.team} size={24} />
                    <span className="font-semibold truncate" style={{ color: TP.text, letterSpacing: "-0.01em" }}>
                      {r.team?.name || "—"}
                    </span>
                    <span
                      className="hidden max-[720px]:inline-block ml-auto text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded"
                      style={{ background: "#F3F5F8", color: TP.text3 }}
                    >
                      {r.team?.short_name || ""}
                    </span>
                  </div>
                </td>
                <td className="text-center px-1 py-2 align-middle" style={{ borderBottom: cellBorder }}>{r.played}</td>
                <td className="text-center px-1 py-2 align-middle hidden sm:table-cell" style={{ borderBottom: cellBorder }}>{r.won}</td>
                <td className="text-center px-1 py-2 align-middle hidden sm:table-cell" style={{ borderBottom: cellBorder }}>{r.drawn}</td>
                <td className="text-center px-1 py-2 align-middle hidden sm:table-cell" style={{ borderBottom: cellBorder }}>{r.lost}</td>
                <td className="text-center px-1 py-2 align-middle hidden md:table-cell" style={{ borderBottom: cellBorder }}>{r.goals_for}</td>
                <td className="text-center px-1 py-2 align-middle hidden md:table-cell" style={{ borderBottom: cellBorder }}>{r.goals_against}</td>
                <td className="text-center px-1 py-2 align-middle hidden md:table-cell font-semibold" style={{ borderBottom: cellBorder, color: gdColor }}>
                  {r.goal_diff > 0 ? "+" : ""}{r.goal_diff}
                </td>
                <td className="text-right pr-4 pl-1 py-2 align-middle font-extrabold" style={{ borderBottom: cellBorder, color: TP.text, fontSize: 14 }}>
                  {r.points}
                </td>
                <td className="text-right pr-4 pl-1 py-2 align-middle hidden lg:table-cell" style={{ borderBottom: cellBorder }}>
                  <FormGuide form={r.form} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton + error state
// ─────────────────────────────────────────────────────────────────────────────
function LoadingSkeleton({ rowCount = 10 }) {
  return (
    <div className="animate-pulse">
      <div className="px-5 py-2 flex gap-2 border-b" style={{ borderColor: TP.border, background: TP.bgSubtle }}>
        {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-6 w-16 rounded-full bg-gray-100" />)}
      </div>
      <div className="divide-y" style={{ borderColor: TP.border }}>
        {Array.from({ length: rowCount }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-5 py-2.5">
            <div className="h-4 w-4 rounded bg-gray-100" />
            <div className="h-6 w-6 rounded bg-gray-100" />
            <div className="h-4 flex-1 rounded bg-gray-100" />
            <div className="h-4 w-6 rounded bg-gray-100" />
            <div className="h-4 w-8 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
      <AlertCircle size={32} style={{ color: TP.loss }} />
      <div>
        <div className="font-semibold text-sm" style={{ color: TP.text }}>Could not load standings</div>
        {message && <div className="text-xs mt-1" style={{ color: TP.text2 }}>{message}</div>}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold"
        style={{ background: TP.primary, borderColor: TP.primary, color: "#fff" }}
      >
        <RefreshCw size={12} />
        Try again
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Footer (legend + powered by)
// ─────────────────────────────────────────────────────────────────────────────
// Derive the legend from the promotion zones actually present in the displayed
// table, deduped by zone id (falling back to name+color). Each entry carries the
// backend-supplied (design-palette) colour and a German display label.
function deriveLegend(rows) {
  const seen = new Map();
  for (const r of rows || []) {
    const p = r?.promotion;
    if (!p || !p.color) continue;
    const key = p.id || `${p.name}|${p.color}`;
    if (seen.has(key)) continue;
    seen.set(key, {
      color: p.color,
      label: ZONE_LABELS[p.id] || p.name || "",
    });
  }
  return Array.from(seen.values()).filter((i) => i.label);
}

function Footer({ rows }) {
  const items = deriveLegend(rows);
  return (
    <div
      className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t"
      style={{ borderColor: TP.border, background: TP.bgSubtle }}
    >
      <div className="flex gap-3 flex-wrap">
        {items.map((i) => (
          <div key={i.label} className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: TP.text2 }}>
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: i.color }} />
            {i.label}
          </div>
        ))}
      </div>
      <div className="inline-flex items-center gap-2 text-[11px] font-medium" style={{ color: TP.text2 }}>
        <span>Powered by</span>
        <img src={tpLogo} alt="TransferPitch" height={16} style={{ height: 16, opacity: 0.95 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// One-time scoped CSS for the pulsing live-pip keyframes
// ─────────────────────────────────────────────────────────────────────────────
const SCOPED_CSS = `
.tp-livepip { position: relative; }
.tp-livepip > span { position: absolute; inset: 0; border-radius: 50%; background: var(--lp); }
.tp-livepip > span:first-child { animation: tpPulse 1.6s ease-out infinite; }
@keyframes tpPulse {
  0%   { transform: scale(1);   opacity: 0.7; }
  80%  { transform: scale(2.6); opacity: 0;   }
  100% { transform: scale(2.6); opacity: 0;   }
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Top-level widget
// ─────────────────────────────────────────────────────────────────────────────
function deriveMatchday(rows) {
  if (!rows || rows.length === 0) return 0;
  return rows.reduce((max, r) => (r.played > max ? r.played : max), 0);
}

const StandingsWidget = () => {
  // Default to Premier League — first entry in TOP5_META.
  const DEFAULT_LEAGUE_ID = "jednm9whz0ryox8";

  const [regions, setRegions] = useState([]);
  const [activeId, setActiveId] = useState(DEFAULT_LEAGUE_ID);
  const [standings, setStandings] = useState(null);
  const [liveMatches, setLiveMatches] = useState([]);
  const [liveMatchday, setLiveMatchday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [tick, setTick] = useState(0);

  // Cup bracket state — populated only when the selected entry is a cup
  // (league.is_cup === true). Rendered IN PLACE OF the standings table.
  const [cup, setCup] = useState(null);
  const [cupLoading, setCupLoading] = useState(false);

  const standingsAbortRef = useRef(null);
  const liveAbortRef = useRef(null);
  const cupAbortRef = useRef(null);

  const flat = useMemo(() => {
    const all = [];
    for (const r of regions) for (const l of (r.leagues || [])) all.push(l);
    return all;
  }, [regions]);

  const activeLeague = useMemo(
    () => flat.find((l) => l.competition_id === activeId) || null,
    [flat, activeId],
  );

  // A selected entry is a cup when the backend tags it with is_cup
  // (derived from TSCompetition.type == 2). Cups show a bracket, not a table.
  const isCup = !!activeLeague?.is_cup;

  // Tracks document.visibilityState so all pollers can skip when the tab
  // is hidden (saves battery + DB load).
  const isVisibleRef = useRef(
    typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  const [visTick, setVisTick] = useState(0); // re-trigger refresh on visibility change
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onChange = () => {
      const visible = document.visibilityState !== "hidden";
      isVisibleRef.current = visible;
      if (visible) setVisTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  // Load league list on mount + refresh every 60 s for live counts.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isVisibleRef.current) return;
      try {
        const res = await dashboardService.getAvailableLeagues();
        if (cancelled) return;
        if (res.success) setRegions(res.data?.regions || []);
      } catch (e) {
        // silent — keep previous regions
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [visTick]);

  // Load standings + live matches (league) OR the knockout bracket (cup)
  // whenever the active entry changes. Cancels every in-flight request first so
  // switching league↔cup never shows a stale view.
  const loadActive = useCallback(async ({ background = false } = {}) => {
    if (!activeLeague) return;
    // Always cancel any in-flight requests (both kinds) before starting.
    if (standingsAbortRef.current) standingsAbortRef.current.abort();
    if (liveAbortRef.current) liveAbortRef.current.abort();
    if (cupAbortRef.current) cupAbortRef.current.abort();

    // ── Cup branch: fetch the bracket, render it in place of the table. ──────
    if (activeLeague.is_cup) {
      const cCtrl = new AbortController();
      cupAbortRef.current = cCtrl;
      // Drop any league table/live data so it can't flash behind the bracket.
      setStandings(null);
      setLiveMatches([]);
      setLiveMatchday(null);
      setError(null);
      if (background) setRefreshing(true); else setCupLoading(true);
      try {
        const res = await dashboardService.getCupBracket(activeLeague.competition_id, {
          signal: cCtrl.signal,
        });
        if (cCtrl.signal.aborted) return;
        if (res.success) {
          setCup(res.data);
        } else {
          setError(res.error || "Could not load cup bracket");
        }
        setLastFetchedAt(Date.now());
      } catch (e) {
        if (e?.name !== "CanceledError") setError(e?.message || "Network error");
      } finally {
        if (!cCtrl.signal.aborted) {
          setCupLoading(false);
          setRefreshing(false);
        }
      }
      return;
    }

    // ── League branch: standings + live matches (unchanged behaviour). ───────
    setCup(null); // clear any previous bracket so it can't linger
    const sCtrl = new AbortController();
    const lCtrl = new AbortController();
    standingsAbortRef.current = sCtrl;
    liveAbortRef.current = lCtrl;

    if (background) setRefreshing(true); else setLoading(true);
    setError(null);

    try {
      const seasonId = activeLeague.season_id;
      if (!seasonId) {
        setStandings({ standings: [], season: { year: "" }, competition: activeLeague });
      } else {
        const res = await dashboardService.getSeasonStandings(seasonId, { signal: sCtrl.signal });
        if (sCtrl.signal.aborted) return;
        if (!res.success) {
          setError(res.error || "Could not load standings");
        } else {
          setStandings(res.data);
        }
      }
      setLastFetchedAt(Date.now());
    } catch (e) {
      if (e?.name !== "CanceledError") setError(e?.message || "Network error");
    } finally {
      if (!sCtrl.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }

    try {
      const lr = await dashboardService.getLiveMatches(activeLeague.competition_id, { signal: lCtrl.signal });
      if (lCtrl.signal.aborted) return;
      if (lr.success) {
        setLiveMatches(lr.data?.results || []);
        setLiveMatchday(lr.data?.matchday || null);
      } else {
        setLiveMatches([]); setLiveMatchday(null);
      }
    } catch (e) {
      if (e?.name !== "CanceledError") { setLiveMatches([]); setLiveMatchday(null); }
    }
  }, [activeLeague]);

  useEffect(() => { loadActive(); }, [loadActive]);

  // 30 s when at least one live match is on, otherwise 5 min.
  // Skips work while the tab is hidden; on visibility return, the visTick
  // change re-runs this effect and immediately fetches fresh data.
  useEffect(() => {
    if (!isVisibleRef.current) return undefined;
    const interval = (liveMatches.length > 0) ? REFRESH_LIVE_MS : REFRESH_NORMAL_MS;
    const id = setInterval(() => {
      if (isVisibleRef.current) loadActive({ background: true });
    }, interval);
    return () => clearInterval(id);
  }, [loadActive, liveMatches.length, visTick]);

  // 5-second ticker for the "vor Xs" counter — pauses when tab is hidden.
  useEffect(() => {
    if (!isVisibleRef.current) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [visTick]);

  const rows = standings?.standings || [];
  const matchday = useMemo(() => deriveMatchday(rows), [rows]);

  const secondsAgo = useMemo(() => {
    if (!lastFetchedAt) return null;
    return Math.max(0, Math.floor((Date.now() - lastFetchedAt) / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFetchedAt, tick]);

  const onSelectLeague = (league) => {
    if (!league || league.competition_id === activeId) return;
    setActiveId(league.competition_id);
  };

  return (
    <div className="bg-white rounded-lg border shadow h-[500px] overflow-hidden flex flex-col" style={{ borderColor: TP.border }}>
      <style>{SCOPED_CSS}</style>
      <style>{CUP_BRACKET_CSS}</style>

      <Topbar regions={regions} activeLeague={activeLeague} onSelectLeague={onSelectLeague} />
      <LeagueHeader league={activeLeague} />
      {/* League-only chrome (season banner / live strip / matchday bar) is
          hidden for cups; cups get a slim matchday-style bar with the legend. */}
      {!isCup && standings?.season?.id && standings.season.is_current === false && (
        <LastSeasonBanner seasonYear={standings.season.year} />
      )}
      {!isCup && <LiveStrip matches={liveMatches} matchday={liveMatchday || matchday} />}
      {isCup ? (
        <CupBar
          season={cup?.season}
          secondsAgo={secondsAgo}
          onRefresh={() => loadActive({ background: true })}
          refreshing={refreshing}
        />
      ) : (
        <MatchdayBar
          matchday={matchday}
          secondsAgo={secondsAgo}
          onRefresh={() => loadActive({ background: true })}
          refreshing={refreshing}
        />
      )}
      <div className="flex-1 overflow-hidden">
        {isCup ? (
          error ? (
            <ErrorState message={error} onRetry={() => loadActive()} />
          ) : (
            <div className="cup-bracketscope h-full">
              <CupBracket cup={cup} loading={cupLoading} />
            </div>
          )
        ) : loading && !standings ? (
          <LoadingSkeleton rowCount={10} />
        ) : error ? (
          <ErrorState message={error} onRetry={() => loadActive()} />
        ) : (
          <StandingsTable rows={rows} />
        )}
      </div>
      {isCup ? <CupFooter /> : <Footer rows={rows} />}
    </div>
  );
};

export default StandingsWidget;
